import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { useEffect, useState } from "react";

import { StyledAnchor } from "@/components/common/styles";
import {
  MID,
  SITE_STEPS,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import { copyToClipboardWrapper } from "@/utils/clipBoardUtils";

const LABEL = "SHARE";
const CONTROL = "36.8px";
const ICON = 17.59;
/** The published copy control is wider than the other three by its padding. */
const COPY_PADDING = "8px 8.8px";
/** The live confirmation is driven by a script we cannot time, so this is ours. */
const COPIED_DWELL = 2000;

/** Below the tablet step the whole strip is a size larger, and gives up its own lead. */
const COMPACT_CONTROL = { height: "40px", backgroundColor: "common.white" };
const COMPACT_COPY_PADDING = "8px 10.4px";

const CONTROL_SX = {
  height: CONTROL,
  maxWidth: "100%",
  borderRadius: "100vw",
  // What the published control leaves to the page and MUI's icon button does
  // not: it gives way with the row, and carries no type of its own.
  flexShrink: 1,
  fontSize: "inherit",
  lineHeight: "inherit",
  border: 1,
  borderColor: marketingPalette.hairline,
  color: marketingPalette.ink,
  "&:hover": {
    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.1),
    borderColor: "primary.dark",
    color: "primary.main",
  },
};

function CopyIcon() {
  return (
    <svg width={ICON} height={ICON} viewBox="0 0 16 16" fill="none">
      <path
        d="M9.02953 6.97064C8.48346 6.4248 7.74297 6.11816 6.97087 6.11816C6.19877 6.11816 5.45827 6.4248 4.9122 6.97064L2.85287 9.02931C2.30678 9.57539 2 10.316 2 11.0883C2 11.8606 2.30678 12.6012 2.85287 13.1473C3.39895 13.6934 4.13959 14.0002 4.91187 14.0002C5.68414 14.0002 6.42478 13.6934 6.97087 13.1473L8.0002 12.118"
        stroke="currentcolor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.9707 9.02953C7.51677 9.57538 8.25727 9.88201 9.02937 9.88201C9.80147 9.88201 10.542 9.57538 11.088 9.02953L13.1474 6.97087C13.6935 6.42479 14.0002 5.68414 14.0002 4.91187C14.0002 4.13959 13.6935 3.39895 13.1474 2.85287C12.6013 2.30678 11.8606 2 11.0884 2C10.3161 2 9.57545 2.30678 9.02937 2.85287L8.00004 3.8822"
        stroke="currentcolor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg width={ICON} height={ICON} viewBox="0 0 16 16" fill="none">
      <path
        d="M14.3999 14.3995V9.71146C14.3999 7.40746 13.9039 5.64746 11.2159 5.64746C9.91993 5.64746 9.05593 6.35146 8.70393 7.02346H8.67193V5.85546H6.12793V14.3995H8.78393V10.1595C8.78393 9.03946 8.99193 7.96746 10.3679 7.96746C11.7279 7.96746 11.7439 9.23146 11.7439 10.2235V14.3835H14.3999V14.3995Z"
        fill="currentcolor"
      />
      <path
        d="M1.80762 5.85547H4.46362V14.3995H1.80762V5.85547Z"
        fill="currentcolor"
      />
      <path
        d="M3.13561 1.59961C2.28761 1.59961 1.59961 2.28761 1.59961 3.13561C1.59961 3.98361 2.28761 4.68761 3.13561 4.68761C3.98361 4.68761 4.67161 3.98361 4.67161 3.13561C4.67161 2.28761 3.98361 1.59961 3.13561 1.59961Z"
        fill="currentcolor"
      />
    </svg>
  );
}

function TwitterIcon() {
  return (
    <svg width={ICON} height={ICON} viewBox="0 0 16 16" fill="none">
      <path
        d="M14.384 4.0318C13.904 4.2398 13.408 4.3838 12.88 4.4478C13.424 4.1278 13.84 3.6158 14.032 2.9918C13.52 3.2958 12.96 3.5038 12.368 3.6318C11.888 3.1198 11.2 2.7998 10.448 2.7998C8.99199 2.7998 7.82399 3.9838 7.82399 5.4238C7.82399 5.6318 7.83999 5.8238 7.88799 6.0158C5.71199 5.9198 3.79199 4.8638 2.49598 3.2798C1.56798 4.9438 2.60798 6.3198 3.29598 6.78381C2.87998 6.78381 2.46398 6.65581 2.11198 6.4638C2.11198 7.75981 3.02398 8.83181 4.20798 9.07181C3.95198 9.1518 3.37598 9.1998 3.02398 9.1198C3.35998 10.1598 4.33598 10.9278 5.47199 10.9438C4.57598 11.6478 3.26398 12.2078 1.58398 12.0318C2.75198 12.7838 4.12798 13.2158 5.61598 13.2158C10.448 13.2158 13.072 9.21581 13.072 5.7598C13.072 5.6478 13.072 5.5358 13.056 5.4238C13.6 5.0238 14.048 4.5598 14.384 4.0318Z"
        fill="currentcolor"
      />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width={ICON} height={ICON} viewBox="0 0 16 16" fill="none">
      <path
        d="M6.46395 8.83221C6.41595 8.83221 5.35995 8.83221 4.87995 8.83221C4.62395 8.83221 4.54395 8.73621 4.54395 8.49621C4.54395 7.85621 4.54395 7.20021 4.54395 6.56021C4.54395 6.30421 4.63995 6.22421 4.87995 6.22421H6.46395C6.46395 6.17621 6.46395 5.24821 6.46395 4.81621C6.46395 4.17621 6.57595 3.56821 6.89595 3.00821C7.23195 2.43221 7.71195 2.04821 8.31995 1.82421C8.71995 1.68021 9.11995 1.61621 9.55195 1.61621H11.1199C11.3439 1.61621 11.4399 1.71221 11.4399 1.93621V3.76021C11.4399 3.98421 11.3439 4.08021 11.1199 4.08021C10.6879 4.08021 10.2559 4.08021 9.82395 4.09621C9.39195 4.09621 9.16795 4.30421 9.16795 4.75221C9.15195 5.23221 9.16795 5.69621 9.16795 6.19221H11.0239C11.2799 6.19221 11.3759 6.28821 11.3759 6.54421V8.48021C11.3759 8.73621 11.2959 8.81621 11.0239 8.81621C10.4479 8.81621 9.21595 8.81621 9.16795 8.81621V14.0322C9.16795 14.3042 9.08795 14.4002 8.79995 14.4002C8.12795 14.4002 7.47195 14.4002 6.79995 14.4002C6.55995 14.4002 6.46395 14.3042 6.46395 14.0642C6.46395 12.3842 6.46395 8.88021 6.46395 8.83221Z"
        fill="currentcolor"
      />
    </svg>
  );
}

/** Every published intent is built from the canonical URL alone; none carries the post title. */
const SHARE_INTENTS = [
  {
    label: "Share on LinkedIn",
    base: "https://www.linkedin.com/sharing/share-offsite/?mini=true&url=",
    Icon: LinkedInIcon,
  },
  {
    label: "Share on X",
    base: "https://twitter.com/intent/tweet?url=",
    Icon: TwitterIcon,
  },
  {
    label: "Share on Facebook",
    base: "https://www.facebook.com/sharer/sharer.php?u=",
    Icon: FacebookIcon,
  },
];

function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = setTimeout(() => setCopied(false), COPIED_DWELL);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <Tooltip
      title="Copied"
      open={copied}
      disableHoverListener
      disableFocusListener
      disableTouchListener
    >
      <IconButton
        aria-label="Copy link to this article"
        onClick={() =>
          copyToClipboardWrapper(url).then(
            () => setCopied(true),
            () => setCopied(false)
          )
        }
        sx={(theme) => ({
          ...CONTROL_SX,
          p: COPY_PADDING,
          fontSize: 14,
          lineHeight: "15px",
          [theme.breakpoints.down(SITE_STEPS.wide)]: {
            ...COMPACT_CONTROL,
            p: COMPACT_COPY_PADDING,
          },
        })}
      >
        <CopyIcon />
      </IconButton>
    </Tooltip>
  );
}

/** The share strip that follows the author card, and the dashed rule under it. */
export default function ShareRow({ url }: { url: string }) {
  const target = encodeURIComponent(url);

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      columnGap={2}
      sx={(theme) => ({
        // A lead of its own on top of the column gap, dropped below the tablet step.
        mt: 3,
        pb: 3,
        borderBottom: "1px dashed",
        borderBottomColor: marketingPalette.hairline,
        [theme.breakpoints.down(SITE_STEPS.wide)]: {
          mt: 0,
          py: "9.6px",
          px: "17.6px",
          backgroundColor: marketingPalette.surface,
          borderRadius: "12px",
        },
        // On a phone the label leaves the icons' line and sits over them.
        [TINY]: { flexDirection: "column", rowGap: "8px" },
      })}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        columnGap={2}
        sx={(theme) => ({
          mt: 3,
          [theme.breakpoints.down(SITE_STEPS.wide)]: {
            mt: "6.4px",
            backgroundColor: marketingPalette.surface,
            borderRadius: "12px",
          },
          // Below the tablet step only the padded outer keeps a lead.
          [MID]: { mt: 0 },
          [TINY]: { mt: 0, flexDirection: "column", rowGap: "8px" },
        })}
      >
        <Typography
          component="span"
          sx={(theme) => ({
            fontSize: 13,
            lineHeight: "19.5px",
            letterSpacing: "1px",
            color: alpha(marketingPalette.inkSubtle, 0.9),
            mr: 2.5,
            [theme.breakpoints.down(SITE_STEPS.wide)]: {
              fontSize: 14,
              lineHeight: "21px",
            },
          })}
        >
          {LABEL}
        </Typography>
        <Stack direction="row" columnGap={2}>
          <CopyLink url={url} />
          {SHARE_INTENTS.map(({ label, base, Icon }) => (
            <IconButton
              key={label}
              aria-label={label}
              component={StyledAnchor}
              href={`${base}${target}`}
              target="_blank"
              rel="nofollow"
              sx={(theme) => ({
                ...CONTROL_SX,
                width: CONTROL,
                p: 0,
                [theme.breakpoints.down(SITE_STEPS.wide)]: {
                  ...COMPACT_CONTROL,
                  width: COMPACT_CONTROL.height,
                },
              })}
            >
              <Icon />
            </IconButton>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
}
